import { ArcStack, ArcSwitch, ArcText, ArcIconButton, ArcButton, ArcOutlinedInput } from "@src/core/ui";
import React, { useEffect } from 'react';
import { RTKSettingsPopupProps } from "./types";
import { ReactComponent as CloseIcon } from '@src/assets/icons/close.svg';
import { useGetNtripQuery, useUpdateNtripMutation } from '@src/features/drone/redux/api';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { EMPTY_VALUE } from '@src/core/config';
import { IUpdateNtripRequest } from '@src/features/drone/redux/types';
import { useTheme, alpha } from '@mui/material';
import { trackError, toTrackedError } from '@src/core/sentry';

interface RTKFormData {
  isRtkActive: boolean;
  ntripHost: string;
  ntripPort: number;
  ntripMountpoint: string;
  ntripUsername: string;
  ntripPassword: string;
}

export const RTKSettingsPopup: React.FC<RTKSettingsPopupProps> = (props) => {
  const { open, onClose, droneId } = props;
  const theme = useTheme();
  const [updateNtrip, { isLoading: isUpdating }] = useUpdateNtripMutation();
  const { data: ntripData, error } = useGetNtripQuery(
    { droneId },
    { skip: !open || !droneId }
  );

  const { control, handleSubmit, watch, reset } = useForm<RTKFormData>({
    defaultValues: {
      isRtkActive: false,
      ntripHost: EMPTY_VALUE,
      ntripPort: '' as any,
      ntripMountpoint: EMPTY_VALUE,
      ntripUsername: EMPTY_VALUE,
      ntripPassword: EMPTY_VALUE,
    }
  });

  useEffect(() => {
    if (ntripData) {
      reset({
        isRtkActive: ntripData.ntripEnabled || false,
        ntripHost: ntripData.ntripHost || EMPTY_VALUE,
        ntripPort: ntripData.ntripPort || ('' as any),
        ntripMountpoint: ntripData.ntripMountpoint || EMPTY_VALUE,
        ntripUsername: ntripData.ntripUsername || EMPTY_VALUE,
        ntripPassword: ntripData.ntripPassword || EMPTY_VALUE,
      });
    }
  }, [ntripData, reset]);

  useEffect(() => {
    if (error) {
      console.error('Get NTRIP error:', error);
    }
  }, [error]);

  const isRtkActive = watch('isRtkActive');

  const onHandleClose = React.useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const onSubmit = React.useCallback((data: RTKFormData) => {
    const getValue = <T,>(value: T): T | null => {
      return data.isRtkActive && value ? value : null;
    };

    const payload: IUpdateNtripRequest = {
      droneId,
      ntripEnabled: data.isRtkActive,
      ntripHost: getValue(data.ntripHost),
      ntripPort: getValue(data.ntripPort),
      ntripMountpoint: getValue(data.ntripMountpoint),
      ntripUsername: getValue(data.ntripUsername),
      ntripPassword: getValue(data.ntripPassword),
    };

    updateNtrip(payload).unwrap()
      .then(() => {
        toast.success('RTK NTRIP settings updated successfully');
        onHandleClose();
      })
      .catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0023', extra: { flow: 'updateNtrip', droneId } });
      });
  }, [droneId, updateNtrip, onHandleClose]);

  if (!open) return null;

  return (
    <>
      <ArcStack
        position='fixed'
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={998}
        sx={{
          backgroundColor: alpha(theme.palette.background.default, 0.75),
          cursor: 'pointer'
        }}
        onClick={onHandleClose}
      />
      <ArcStack 
        width='100%' 
        maxWidth={480}
        minWidth={400}
        borderRadius={1}
        position='fixed'
        left='52%'
        top='50%'
        sx={{
          transform: 'translate(-50%, -50%)',
        }}
        zIndex={999}
        bgcolor='background.paper'
        padding={3}
        component='form'
        onSubmit={handleSubmit(onSubmit)}>

        <ArcStack marginTop={0.5}>
          <ArcIconButton sx={{ marginLeft: 'auto', padding: 0 }} onClick={onHandleClose}>
            <CloseIcon />
          </ArcIconButton>
        </ArcStack>

        <ArcText.h5 marginTop={1} fontWeight={500} fontSize={16}>
        RTK NTRIP Settings
        </ArcText.h5>
      
        <ArcText.caption marginTop={0.5} fontSize={14}>
        Configure NTRIP connection parameters for RTK positioning
        </ArcText.caption>

        <ArcStack justifyContent='space-between' direction='row' alignItems='center' marginTop={2}>
          <ArcText color='white' fontSize={14}>RTK NTRIP</ArcText>
          <Controller
            name="isRtkActive"
            control={control}
            render={({ field }) => (
              <ArcSwitch.brand
                checked={field.value}
                onChange={field.onChange}
                sx={{
                  '& .MuiSwitch-track': { backgroundColor: 'background.default' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'background.default',
                  },
                }}
              />
            )}
          />
        </ArcStack>

        {isRtkActive && (
          <ArcStack spacing={1} marginTop={1}>  
            <Controller
              name="ntripHost"
              control={control}
              render={({ field }) => (
                <RTKInput name='NTRIP Host' value={field.value} type='text' onChange={field.onChange} />
              )}
            />
            <Controller
              name="ntripPort"
              control={control}
              render={({ field }) => (
                <RTKInput 
                  name='NTRIP Port' 
                  value={field.value} 
                  type='number' 
                  onChange={(value) => field.onChange(Number(value))} 
                />
              )}
            />
            <Controller
              name="ntripMountpoint"
              control={control}
              render={({ field }) => (
                <RTKInput name='NTRIP Mountpoint' value={field.value} type='text' onChange={field.onChange} />
              )}
            />
            <Controller
              name="ntripUsername"
              control={control}
              render={({ field }) => (
                <RTKInput name='NTRIP Username' value={field.value} type='text' onChange={field.onChange} />
              )}
            />
            <Controller
              name="ntripPassword"
              control={control}
              render={({ field }) => (
                <RTKInput name='NTRIP Password' value={field.value} type='text' onChange={field.onChange} />
              )}
            />
          </ArcStack>
        )}

        <ArcStack direction='row' spacing={1} justifyContent='flex-end' marginTop={2}>
          <ArcButton.tertiary type="button" onClick={onHandleClose}> 
          Cancel 
          </ArcButton.tertiary> 
          <ArcButton.brand type='submit' isLoading={isUpdating} disabled={isUpdating}> 
          OK 
          </ArcButton.brand>
        </ArcStack>
      </ArcStack>
    </>
  );
};

interface RTKInputProps {
  name: string;
  value: string | number;
  type: 'text' | 'number';
  onChange: (value: string | number) => void;
}

const RTKInput: React.FC<RTKInputProps> = React.memo((props) => {
  RTKInput.displayName = 'RTKInput';
  const { name, value, type, onChange } = props;

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = type === 'number' ? Number(event.target.value) : event.target.value;
    onChange(newValue);
  }, [type, onChange]);

  return (
    <ArcStack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
      <ArcText color='white' fontSize={14}>{name}</ArcText>
      <ArcOutlinedInput 
        value={value} 
        onChange={handleChange} 
        type={type} 
        inputMode={type === 'number' ? 'numeric' : 'text'} 
        sx={{ width: 250 }} 
      />
    </ArcStack>
  );
});